import { pushApi } from "@/lib/apiClient";

const SW_URL = "/sw.js";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

export function pushSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
}

export async function registerServiceWorker() {
    if (!pushSupported()) return null;
    try {
        const reg = await navigator.serviceWorker.register(SW_URL);
        return reg;
    } catch (e) {
        console.warn("SW register failed", e);
        return null;
    }
}

export async function currentPushStatus() {
    if (!pushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
    const permission = Notification.permission;
    let subscribed = false;
    try {
        const reg = await navigator.serviceWorker.getRegistration(SW_URL);
        if (reg) {
            const sub = await reg.pushManager.getSubscription();
            subscribed = !!sub;
        }
    } catch (_) {}
    return { supported: true, permission, subscribed };
}

export async function enablePush() {
    if (!pushSupported()) throw new Error("Push notifications are not supported in this browser");
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error("permission-denied");

    const reg = (await navigator.serviceWorker.getRegistration(SW_URL)) || (await registerServiceWorker());
    if (!reg) throw new Error("sw-register-failed");

    const { public_key } = await pushApi.publicKey();
    if (!public_key) throw new Error("no-public-key");

    let sub;
    try {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(public_key),
        });
    } catch (e) {
        // If already subscribed with a different key, unsubscribe and retry once
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
            await existing.unsubscribe();
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(public_key),
            });
        } else {
            throw e;
        }
    }

    const j = sub.toJSON();
    await pushApi.subscribe({
        endpoint: sub.endpoint,
        keys: { p256dh: j.keys.p256dh, auth: j.keys.auth },
        user_agent: navigator.userAgent,
    });
    return sub;
}

export async function disablePush() {
    if (!pushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    try {
        await pushApi.unsubscribe(sub.endpoint);
    } catch (_) {}
    await sub.unsubscribe();
}
